import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Prompt } from 'react-router-dom';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import FirebaseContext from '../../../../../context/FirebaseContext';
import ExerciseContext from '../../../../context/ExerciseContext';
import ExerciseTagContext from '../../../../context/ExerciseTagContext';
import Exercise from '../../../../Model/Exercise';
import useToast from '../../../../hooks/useToast';
import useBeforeUnloadGuard from '../../../../../hooks/useBeforeUnloadGuard';
import { deleteVimeoVideo } from '../../../../utils/vimeo';
import { buildTagOptions } from '../../../../utils/tags';
import { transformVideoUrl } from '../../../../../utils/video';
import {
  UploadStatus,
  SaveStatus,
  FILE_INPUT_ACCEPT,
} from './constants';
import {
  buildUpload,
  getCardView,
  summarize,
  orderCards,
} from './utils';
import useVideoUploadQueue from './useVideoUploadQueue';
import useSaveExercises from './useSaveExercises';
import useCardFiles from './useCardFiles';
import useSessionPersistence from './useSessionPersistence';
import texts from './texts';
import DropZone from './components/DropZone';
import AutoSaveToggle from './components/AutoSaveToggle';
import ExerciseCard from './components/ExerciseCard';
import Footer from './components/Footer';
import ConfirmModals from './components/ConfirmModals';
import {
  Container,
  PageTitleRow,
  PageTitle,
  CountChip,
  Intro,
  Body,
  EmptyNote,
} from './styles';

// Stable fallback while the exercise library snapshot hasn't been captured yet.
const EMPTY_EXERCISES = [];

const BulkExerciseCreator = ({ onClose }) => {
  const [cards, setCards] = useState([]);
  const [modal, setModal] = useState(null); // { type: 'leave' } | { type: 'remove', id }
  const [autoSave, setAutoSave] = useState(true);
  // Drag state + the drop-zone file input live inside the shared FileDropZone.

  const { exercises } = useContext(ExerciseContext);
  const { tags } = useContext(ExerciseTagContext);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { coachDoc } = useContext(ExternalCoachContext);
  const coachId = coachDoc?.id;
  const coachName = coachDoc?.name;
  const { showToast } = useToast();

  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  const autoSaveRef = useRef(autoSave);
  autoSaveRef.current = autoSave;
  const saveCardRef = useRef(null); // latest saveCard, for the upload-complete callback

  // The duplicate-name check runs against the library as it was when the view
  // opened, NOT the live list. The live listener adds each saved exercise to
  // `exercises` before the card's own exerciseId lands in state, which flashed
  // a false "already exists" error mid-save. Exercises created by this batch
  // are covered by the in-batch duplicate check instead (saved cards stay in
  // the card list).
  const exercisesSnapshotRef = useRef(null);
  if (exercisesSnapshotRef.current === null && exercises && exercises.length) {
    exercisesSnapshotRef.current = exercises
      .filter((exercise) => exercise.name)
      .map((exercise) => ({ id: exercise.id, name: exercise.name }));
  }
  const existingExercises = exercisesSnapshotRef.current || EMPTY_EXERCISES;
  const existingExercisesRef = useRef(existingExercises);
  existingExercisesRef.current = existingExercises;

  const tagOptions = useMemo(() => buildTagOptions(tags), [tags]);

  // Revoke object URLs on unmount.
  useEffect(() => () => {
    cardsRef.current.forEach((card) => {
      if (card.previewUrl) {
        URL.revokeObjectURL(card.previewUrl);
      }
    });
  }, []);

  const updateCard = useCallback((id, updater) => {
    setCards((previous) => previous.map((card) => (card.id === id ? updater(card) : card)));
  }, []);

  const updateUpload = useCallback((id, patch) => {
    updateCard(id, (card) => ({ ...card, upload: { ...card.upload, ...patch } }));
  }, [updateCard]);

  // Best-effort Vimeo cleanup when an uploaded video is discarded. Failures are
  // only logged — an orphaned unsaved upload also expires on Vimeo's side.
  const cleanUpVimeoVideo = useCallback((videoId) => {
    deleteVimeoVideo(videoId, coachId, remote).catch((error) => {
      Sentry.captureException(error, {
        extra: { message: 'Failed to delete Vimeo video during bulk-create cleanup', videoId, coachId },
      });
    });
  }, [coachId, remote]);

  /* ---------- upload engine ---------- */
  const handleUploaded = useCallback((cardId) => {
    if (!autoSaveRef.current) {
      return;
    }
    // Defer one tick so the UPLOADED state patch is applied before the
    // ready-check inside saveCard runs against the latest cards.
    setTimeout(() => {
      if (saveCardRef.current) {
        saveCardRef.current(cardId);
      }
    }, 0);
  }, []);

  const { enqueue, cancel } = useVideoUploadQueue({
    coachId,
    coachName,
    remote,
    onUpdate: updateUpload,
    onUploaded: handleUploaded,
  });

  /* ---------- file intake (drop zone + per-card picker) ---------- */
  const {
    fileInputRef,
    appendCards,
    handleAddVideo,
    handleFileInputChange,
  } = useCardFiles({
    cardsRef,
    setCards,
    updateCard,
    enqueue,
    cancel,
    cleanUpVimeoVideo,
  });

  /* ---------- save lifecycle ---------- */
  const { saveCard, saveReadyCards, retryFailedSaves } = useSaveExercises({
    cardsRef,
    existingExercisesRef,
    updateCard,
  });
  saveCardRef.current = saveCard;

  /* ---------- session recovery ---------- */
  // A restored card's local preview died with the page, but once its exercise
  // doc has a processed video URL we can embed that instead. Best-effort.
  const loadRemoteVideoPreview = useCallback(async (card) => {
    try {
      const exerciseDoc = await Exercise.getExerciseById(card.exerciseId);
      const videoUrl = exerciseDoc && exerciseDoc.exists ? exerciseDoc.videoUrl : '';
      if (videoUrl) {
        updateCard(card.id, (item) => ({ ...item, remoteVideoUrl: transformVideoUrl(videoUrl) }));
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { message: 'Failed to load restored exercise video preview', exerciseId: card.exerciseId },
      });
    }
  }, [updateCard]);

  // On restore, surface what happened, pull remote previews for cards whose
  // exercise already exists, and — when the interrupted session had auto-save
  // on — immediately save cards whose video already reached Vimeo (their
  // bytes survived; only the browser state was lost).
  const handleSessionRestored = useCallback((restoredCards, restoredAutoSave) => {
    showToast(texts.toastSessionRestored);
    restoredCards.forEach((card) => {
      if (card.exerciseId && card.upload.videoId) {
        loadRemoteVideoPreview(card);
      }
    });
    if (!restoredAutoSave) {
      return;
    }
    setTimeout(() => {
      restoredCards.forEach((card) => {
        if (card.save !== SaveStatus.SAVED && card.upload.status === UploadStatus.UPLOADED) {
          saveCardRef.current(card.id);
        }
      });
    }, 0);
  }, [showToast, loadRemoteVideoPreview]);

  const { discardSession } = useSessionPersistence({
    cards,
    cardsRef,
    setCards,
    autoSave,
    setAutoSave,
    onRestore: handleSessionRestored,
  });

  // Intentional exits (Done / Cancel / Leave anyway) drop the recovery
  // snapshot — only unexpected closes should restore.
  const handleClose = () => {
    discardSession();
    onClose();
  };

  /* ---------- card handlers ---------- */
  const handleFieldChange = (id, field, value) => {
    updateCard(id, (card) => ({
      ...card,
      [field]: value,
      // Editing a save-failed card returns it to a saveable draft (its
      // exerciseId, if any, is retained so retrying never duplicates the doc).
      save: card.save === SaveStatus.SAVE_FAILED ? SaveStatus.IDLE : card.save,
    }));
  };

  const handleTagsChange = (id, selectedTags) => {
    updateCard(id, (card) => ({ ...card, tags: selectedTags }));
  };

  const handleToggleCollapse = (id) => {
    updateCard(id, (card) => ({ ...card, collapsed: !card.collapsed }));
  };

  const handleEdit = (id) => {
    updateCard(id, (card) => ({ ...card, collapsed: false }));
  };

  const handleRetryUpload = (id) => {
    const card = cardsRef.current.find((item) => item.id === id);
    if (!card || !card.file || !card.upload || card.upload.status !== UploadStatus.FAILED) {
      return;
    }
    const { videoId } = card.upload;
    updateUpload(id, { status: UploadStatus.QUEUED, error: null });
    enqueue(id, card.file, { videoId });
  };

  const handleRemoveVideo = (id) => {
    const card = cardsRef.current.find((item) => item.id === id);
    cancel(id);
    if (card && card.upload && card.upload.videoId) {
      cleanUpVimeoVideo(card.upload.videoId);
    }
    updateCard(id, (item) => ({ ...item, upload: buildUpload(null) }));
    showToast(texts.toastVideoRemoved);
  };

  const handleRequestRemove = (id) => setModal({ type: 'remove', id });

  const confirmRemove = (id) => {
    const card = cardsRef.current.find((item) => item.id === id);
    if (card) {
      cancel(id);
      // A saved card's video belongs to the created exercise — only clean up
      // videos that never made it into a saved exercise.
      if (card.save !== SaveStatus.SAVED && card.upload && card.upload.videoId) {
        cleanUpVimeoVideo(card.upload.videoId);
      }
      if (card.previewUrl) {
        URL.revokeObjectURL(card.previewUrl);
      }
    }
    setCards((previous) => previous.filter((item) => item.id !== id));
    setModal(null);
    showToast(texts.toastExerciseRemoved);
  };

  /* ---------- footer / leave handlers ---------- */
  const handleLeave = () => setModal({ type: 'leave' });

  const confirmLeave = () => {
    setModal(null);
    handleClose();
    showToast(texts.toastBatchDiscarded);
  };

  // Per-card status/validation, derived once (id-keyed) and reused by every card
  // and the footer summary — avoids recomputing across badge, Save button, counts.
  const cardViews = useMemo(() => {
    const map = new Map();
    cards.forEach((card, index) => map.set(card.id, getCardView(card, index, cards, existingExercises)));
    return map;
  }, [cards, existingExercises]);

  const summary = useMemo(() => summarize([...cardViews.values()]), [cardViews]);
  const orderedCards = useMemo(() => orderCards(cards), [cards]);
  const isDirty = summary.unsaved > 0;

  // Browser close / refresh guard while there is unsaved work. In-app router
  // navigation is covered by <Prompt> below; the footer Leave button shows the
  // richer ConfirmDialog.
  useBeforeUnloadGuard(isDirty);

  return (
    <Container>
      <Prompt
        when={isDirty}
        message={texts.navGuardMessage}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_INPUT_ACCEPT}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      <PageTitleRow>
        <PageTitle>{texts.title}</PageTitle>
        {cards.length > 0 && (
          <CountChip>{format(texts.batchChip, { count: cards.length })}</CountChip>
        )}
      </PageTitleRow>
      <Intro>{autoSave ? texts.introAutoSave : texts.introManual}</Intro>

      <Body>
        <AutoSaveToggle checked={autoSave} onChange={setAutoSave} />

        <DropZone
          isEmpty={cards.length === 0}
          onFiles={appendCards}
        />

        {cards.length === 0
          ? <EmptyNote>{texts.emptyNote}</EmptyNote>
          : orderedCards.map(({ card, index }) => (
            <ExerciseCard
              key={card.id}
              card={card}
              number={index + 1}
              view={cardViews.get(card.id)}
              tagOptions={tagOptions}
              onFieldChange={handleFieldChange}
              onTagsChange={handleTagsChange}
              onToggleCollapse={handleToggleCollapse}
              onSave={saveCard}
              onRemove={handleRequestRemove}
              onRetrySave={saveCard}
              onRetryUpload={handleRetryUpload}
              onRemoveVideo={handleRemoveVideo}
              onAddVideo={handleAddVideo}
              onEdit={handleEdit}
            />
          ))}
      </Body>

      <Footer
        summary={summary}
        onSaveReady={saveReadyCards}
        onRetryFailed={retryFailedSaves}
        onLeave={handleLeave}
        onDone={handleClose}
        onCancel={handleClose}
      />

      <ConfirmModals
        modal={modal}
        cards={cards}
        summary={summary}
        onDismiss={() => setModal(null)}
        onConfirmLeave={confirmLeave}
        onConfirmRemove={confirmRemove}
      />
    </Container>
  );
};

BulkExerciseCreator.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default observer(BulkExerciseCreator);
